// NodeSchemaFields.js

import { normalizeCalendarColor } from 'utilities/CalendarFieldColor';
import { applyChecklistTemplateFieldProps } from 'utilities/ChecklistTemplateItems';
import { normalizeCurrencyCode } from 'utilities/Currency';
import { isRelationFieldType, isEnumCompatibleFieldType, isOptionsListFieldType } from 'utilities/FieldTypes';
import { normalizeRelationDisplayFields } from 'utilities/RelationDisplayFields';
import { isTemporalFieldType } from 'views/utils/temporalFieldFormat';

export function createSchemaField({
  name,
  label,
  fieldDescription,
  type,
  targetType,
  allowMultiple,
  showFilter,
  displayFields,
  showAccess,
  showAsProfileStack,
  checklistItems,
  allowCustomItems,
  defaultValue,
  defaultUnit,
  currencyCode,
  options,
  enumOptionsSort,
  calendarColor,
  displayInDefaultLists,
  source,
} = {}) {
  const field = {
    name,
    label,
    fieldDescription: String(fieldDescription || '').trim(),
    type,
    default: defaultValue,
    options,
    secured: false,
    visible: true,
    required: false,
    indexed: false,
    workspaceField: false,
    includeInQrPrint: false,
    logged: false,
    writable: true,
    columnWidth: null,
    hidden: false,
  };

  if (isOptionsListFieldType(type)) {
    field.displayAsPill = true;
  }

  if (isEnumCompatibleFieldType(type)) {
    field.enumOptionsSort = typeof enumOptionsSort === 'string' && enumOptionsSort.trim().length
      ? enumOptionsSort.trim().toLowerCase()
      : 'manual';
  }

  if (isRelationFieldType(type)) {
    field.targetType = String(targetType || '').trim();
    field.allowMultiple = allowMultiple !== false;
    if (field.allowMultiple === false && showFilter === true) {
      field.showFilter = true;
    }
    const nextDisplayFields = normalizeRelationDisplayFields(displayFields);
    if (nextDisplayFields.length) {
      field.displayFields = nextDisplayFields;
    }
  }

  if (type === 'people') {
    if (showAccess === true) field.showAccess = true;
    if (showAsProfileStack === true) field.showAsProfileStack = true;
  }

  if (type === 'checklist') {
    applyChecklistTemplateFieldProps(field, { checklistItems, allowCustomItems });
  }

  if (type === 'measurement') {
    field.defaultUnit = String(defaultUnit || '').trim();
  }

  if (type === 'money') {
    field.currencyCode = normalizeCurrencyCode(currencyCode);
  }

  if (isTemporalFieldType(type)) {
    field.calendarColor = normalizeCalendarColor(calendarColor);
  }

  if (displayInDefaultLists === true) {
    field.displayInDefaultLists = true;
  }

  if (source) {
    field.source = source;
  }

  if (!field.fieldDescription) {
    delete field.fieldDescription;
  }

  return field;
}

export function insertSchemaFieldAtVisibleIndex(baseFields, visibleFields, index, newField) {
  const base = Array.isArray(baseFields) ? [...baseFields] : [];
  const visible = Array.isArray(visibleFields) ? visibleFields : [];
  const safeIndex = typeof index === 'number' ? index : visible.length;

  if (safeIndex <= 0) {
    base.unshift(newField);
    return base;
  }

  if (safeIndex >= visible.length) {
    base.push(newField);
    return base;
  }

  const anchor = visible[safeIndex];
  if (!anchor?.name) {
    base.push(newField);
    return base;
  }

  const baseIndex = base.findIndex((field) => field?.name === anchor.name);
  if (baseIndex < 0) {
    base.push(newField);
    return base;
  }

  base.splice(baseIndex, 0, newField);
  return base;
}